import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './StakingTierSection.css';

interface Tier {
  id: number;
  amount: string;
  apr: string;
  status: 'filled' | 'active' | 'locked';
  progress: number;
  filledAmount?: string;
  remainingAmount?: string;
}

const StakingTierSection: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [timeProgress, setTimeProgress] = useState(0);

  useEffect(() => {
    const calculateTime = () => {
      // Start: Feb 12, 2026 | End: Mar 14, 2026 (30 days)
      const startDate = new Date('2026-02-12T00:00:00').getTime();
      const targetDate = new Date('2026-03-14T00:00:00').getTime();
      const now = new Date().getTime();
      const totalDuration = targetDate - startDate;
      const elapsed = now - startDate;
      const remaining = targetDate - now;

      if (remaining <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setTimeProgress(100);
        return;
      }

      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
      
      // Progress based on time elapsed
      const progress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
      setTimeProgress(progress);
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Static data for now, representing the contest state
  // Logic: Tier 1 is filling, others are locked
  const tiers: Tier[] = [
    {
      id: 1,
      amount: '50M USD.P',
      apr: '25%',
      status: 'active',
      progress: timeProgress,
      filledAmount: 'Time Elapsed',
      remainingAmount: 'Ends Mar 14'
    },
    {
      id: 2,
      amount: '100M USD.P',
      apr: '20%',
      status: 'locked',
      progress: 0
    },
    {
      id: 3,
      amount: '150M USD.P',
      apr: '15%',
      status: 'locked',
      progress: 0
    },
    {
      id: 4,
      amount: '200M USD.P',
      apr: '7.5%',
      status: 'locked',
      progress: 0
    },
    {
      id: 5,
      amount: '500M USD.P',
      apr: '6%',
      status: 'locked',
      progress: 0
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section className="staking-tiers section-padding">
      <div className="container">
        <div className="tiers-header">
          <motion.h2 
            className="tiers-title"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Staking Prize Contest
          </motion.h2>
          <motion.p 
            className="tiers-subtitle"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Join early to secure the highest APRs. Vaults fill up sequentially—don't miss out on the 25% APR tier!
          </motion.p>
        </div>

        <motion.div 
          className="tiers-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {tiers.map((tier) => (
            <motion.div
              key={tier.id}
              className={`tier-card ${tier.status}`}
              variants={itemVariants}
              whileHover={tier.status === 'active' ? { scale: 1.03, y: -5 } : {}}
            >
              <div className="tier-card-header">
                <div className={`tier-badge ${tier.status}`}>
                  {tier.status === 'active' ? 'Starting soon' : tier.status}
                </div>
              </div>
              
              <div className="tier-amount">{tier.amount}</div>

              <div className="tier-apr">{tier.apr} APR</div>

              <div className="tier-progress-container">
                {tier.status === 'active' ? (
                  <>
                    <div className="countdown-header">
                      <span className="countdown-label">Time Remaining</span>
                      <div className="countdown-timer">
                        <span>{timeLeft.days}d</span> :
                        <span>{String(timeLeft.hours).padStart(2, '0')}h</span> :
                        <span>{String(timeLeft.minutes).padStart(2, '0')}m</span> :
                        <span>{String(timeLeft.seconds).padStart(2, '0')}s</span>
                      </div>
                    </div>
                    <div className="progress-track">
                      <motion.div
                        className="tier-progress-bar"
                        initial={{ width: 0 }}
                        animate={{ width: `${timeProgress}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* Placeholder for inactive tiers if needed, or just empty track */}
                    <div className="progress-track">
                      <div className="tier-progress-bar" style={{ width: '0%' }} />
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="contest-info"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <div className="info-text">
            <h3>How it works</h3>
            <p>
              The Staking Prize Contest rewards early adopters. As each vault fills up, the APR for the next tier unlocks. 
              Stake your USD.P now to lock in the current high rate before it's gone.
            </p>
          </div>
          <motion.button 
            className="btn btn-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const vaultsSection = document.querySelector('.vaults');
              if (vaultsSection) {
                vaultsSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Start Staking Now
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default StakingTierSection;